/**
 * Boats (vanilla Boat): a 1.375 x 0.5625 box that floats on water, rides over
 * land with the ground's slipperiness and is paddled by its rider. Not a Mob:
 * `BoatSim` owns the boats, ticks them at 20 Hz and exposes them as `Rideable`s
 * for the shared mount model in game/riding.ts. DOM-free (node smoke tests run it).
 *
 * Physics is Boat.tick ported 1:1: status (in water / under water / on land /
 * in air) -> `floatBoat` (per-status friction, buoyancy settles the hull ~65%
 * submerged) -> `controlBoat` (W +0.04, S -0.005, A/D +-1 deg turn impulse with
 * the 0.9 water decay, turning alone +0.005) -> move with block collision.
 * Yaw uses the player's convention: forward = (-sin yaw, -cos yaw), yaw grows
 * to the left.
 *
 * Left out vs vanilla: boat-boat / mob push, the second seat being filled by a
 * walking mob (the seat exists; `mountMob` from game/riding.ts fills it).
 */

import { collisionOf, FULL_CELL_BOXES, ICE_ID, PACKED_ICE_ID, BLUE_ICE_ID, IS_WATERY, IS_WATER_SOURCE } from '../../blocks';
import { clipMove, type ShapesAt } from '../../world/collide';
import { waterHeight, waterFlow } from '../../world/fluid';
import { I } from '../../game/itemIds';
import { PLAYER, type Rideable, type RiderInput } from '../../game/riding';

export const BOAT_WOODS = ['oak', 'spruce', 'birch', 'jungle', 'acacia', 'dark_oak', 'mangrove', 'cherry', 'pale_oak'] as const;
export type BoatWood = (typeof BOAT_WOODS)[number];
export const BOAT_ITEMS: readonly number[] = [
  I.OAK_BOAT, I.SPRUCE_BOAT, I.BIRCH_BOAT, I.JUNGLE_BOAT, I.ACACIA_BOAT, I.DARK_OAK_BOAT, I.MANGROVE_BOAT, I.CHERRY_BOAT, I.PALE_OAK_BOAT,
];
/** Wood of a boat item (-1: not a boat item). */
export const boatWoodOf = (item: number): number => BOAT_ITEMS.indexOf(item);

export const BOAT_W = 1.375, BOAT_H = 0.5625;
const HALF = BOAT_W / 2;
const EPS = 1e-7;
/** Vanilla getPassengersRidingOffset (-0.1) + a player's own riding offset (-0.35). */
export const SEAT_DROP_PLAYER = -0.45, SEAT_DROP_MOB = -0.1;
/** Damage (x10 per hit point) at which a boat comes apart. */
const BREAK_DAMAGE = 40;

export type BoatStatus = 'water' | 'under' | 'underFlow' | 'land' | 'air';
export type BlockReader = (x: number, y: number, z: number) => number;

const SLIP = new Map<number, number>([[ICE_ID, 0.98], [PACKED_ICE_ID, 0.98], [BLUE_ICE_ID, 0.989]]);

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

export class Boat implements Rideable {
  readonly kind = 'boat';
  readonly seatCount = 2;
  readonly riderPose = 'sit' as const;
  readonly riderEyeOffset = SEAT_DROP_PLAYER;
  riders: (unknown | null)[] = [null, null];
  removed = false;
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  yaw: number; pyaw: number;
  vx = 0; vy = 0; vz = 0;
  onGround = false;
  status: BoatStatus = 'air';
  oldStatus: BoatStatus = 'air';
  /** Pending turn impulse in degrees (decays by the water friction). */
  deltaRot = 0;
  damage = 0; hurtTime = 0; hurtDir = 1;
  fallDistance = 0;
  /** Ticks spent under water (60 throws the riders out). */
  underTicks = 0;
  waterLevel = 0;
  landFriction = 0;
  /** Paddle animation phase per side [left, right] (radians), previous tick's too. */
  paddle = [0, 0]; paddlePrev = [0, 0];
  inL = false; inR = false; inUp = false; inDown = false;
  /** Left / right paddle moving this tick. */
  pl = false; pr = false;

  constructor(readonly id: number, readonly wood: number, x: number, y: number, z: number, yaw: number) {
    this.x = this.px = x; this.y = this.py = y; this.z = this.pz = z;
    this.yaw = this.pyaw = yaw;
  }

  get item(): number { return BOAT_ITEMS[this.wood] ?? I.OAK_BOAT; }
  get texture(): BoatWood { return BOAT_WOODS[this.wood] ?? 'oak'; }
  get hasPlayer(): boolean { return this.riders.includes(PLAYER); }
  get occupied(): boolean { return this.riders.some((r) => r != null); }

  steer(input: RiderInput): void {
    this.inL = input.strafe < 0; this.inR = input.strafe > 0;
    this.inUp = input.forward > 0; this.inDown = input.forward < 0;
  }
  clearInput(): void { this.inL = this.inR = this.inUp = this.inDown = false; }

  seatPos(i: number, out: { x: number; y: number; z: number }): void {
    const two = this.riders[0] != null && this.riders[1] != null;
    const f = two ? (i === 0 ? 0.2 : -0.6) : 0;
    out.x = this.x - Math.sin(this.yaw) * f;
    out.z = this.z - Math.cos(this.yaw) * f;
    out.y = this.y + (this.riders[i] === PLAYER ? SEAT_DROP_PLAYER : SEAT_DROP_MOB);
  }

  /** World the boat lives in; set by BoatSim (dismountPos needs the blocks). */
  read: BlockReader = () => 0;
  dismountPos(out: { x: number; y: number; z: number }): void {
    const r = this.read;
    const free = (x: number, y: number, z: number): boolean =>
      collisionOf(r(x, y - 1, z)).length > 0 && collisionOf(r(x, y, z)).length === 0 && collisionOf(r(x, y + 1, z)).length === 0
      && IS_WATERY[r(x, y, z)] === 0;
    const fy = Math.floor(this.y);
    for (const rad of [HALF + 0.9, HALF + 1.6]) {
      // sides first (vanilla prefers stepping out beside the hull), then ahead / behind
      for (let k = 0; k < 8; k++) {
        const a = this.yaw + Math.PI / 2 + (k % 2 ? Math.PI : 0) + Math.floor(k / 2) * (Math.PI / 4);
        const x = this.x - Math.sin(a) * rad, z = this.z - Math.cos(a) * rad;
        for (const dy of [0, 1, -1]) {
          const by = fy + dy;
          if (free(Math.floor(x), by, Math.floor(z))) { out.x = x; out.y = by; out.z = z; return; }
        }
      }
    }
    // open water all round: stand up from the seat
    out.x = this.x; out.y = this.y + 0.2; out.z = this.z;
  }

  /** Damage from a punch of `amount` hit points; true when the boat should break. */
  hurt(amount: number, creative: boolean): boolean {
    this.hurtDir = -this.hurtDir || 1;
    this.hurtTime = 10;
    this.damage += amount * 10;
    return creative || this.damage > BREAK_DAMAGE;
  }

  aabb(): number[] { return [this.x - HALF, this.y, this.z - HALF, this.x + HALF, this.y + BOAT_H, this.z + HALF]; }

  /** Ray (unit dir) vs the box: distance to the entry point, or -1. */
  rayDist(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, max: number): number {
    const b = this.aabb(), o = [ox, oy, oz], d = [dx, dy, dz];
    let t0 = 0, t1 = max;
    for (let a = 0; a < 3; a++) {
      if (Math.abs(d[a]) < 1e-9) { if (o[a] < b[a] || o[a] > b[a + 3]) return -1; continue; }
      let ta = (b[a] - o[a]) / d[a], tb = (b[a + 3] - o[a]) / d[a];
      if (ta > tb) { const t = ta; ta = tb; tb = t; }
      t0 = Math.max(t0, ta); t1 = Math.min(t1, tb);
      if (t0 > t1) return -1;
    }
    return t0;
  }
}

export interface BoatEvents {
  /** Boat destroyed by a hard landing / damage / void: caller drops the item. */
  onBreak?: (b: Boat, dropItem: boolean) => void;
}

export class BoatSim {
  boats: Boat[] = [];
  private nextId = 1;
  private readonly shapesAt: ShapesAt;
  private readonly flow = [0, 0, 0];
  events: BoatEvents = {};

  /** `read` returns the block id in a cell; `loaded(x, z)` says whether the column exists (unloaded boats freeze). */
  constructor(private read: BlockReader, private loaded: (x: number, z: number) => boolean = () => true, private readonly allocId?: () => number) {
    this.shapesAt = (x, y, z) => (y < 0 ? FULL_CELL_BOXES : collisionOf(this.read(x, y, z)));
  }

  setReader(read: BlockReader, loaded?: (x: number, z: number) => boolean): void {
    this.read = read; if (loaded) this.loaded = loaded;
    for (const b of this.boats) b.read = read;
  }

  /** Put a boat at the spot unless something solid is in the way. */
  place(wood: number, x: number, y: number, z: number, yaw: number): Boat | null {
    const probe = [x - HALF, y + 0.001, z - HALF, x + HALF, y + BOAT_H, z + HALF];
    for (let cy = Math.floor(probe[1]); cy <= Math.floor(probe[4]); cy++)
      for (let cz = Math.floor(probe[2]); cz <= Math.floor(probe[5]); cz++)
        for (let cx = Math.floor(probe[0]); cx <= Math.floor(probe[3]); cx++) {
          const s = this.shapesAt(cx, cy, cz);
          for (let k = 0; k < s.length; k += 6) {
            if (s[k] / 16 + cx < probe[3] && s[k + 3] / 16 + cx > probe[0] && s[k + 1] / 16 + cy < probe[4] && s[k + 4] / 16 + cy > probe[1]
              && s[k + 2] / 16 + cz < probe[5] && s[k + 5] / 16 + cz > probe[2]) return null;
          }
        }
    return this.add(wood, x, y, z, yaw);
  }

  add(wood: number, x: number, y: number, z: number, yaw: number): Boat {
    const b = new Boat(this.allocId ? this.allocId() : this.nextId++, wood, x, y, z, yaw);
    b.read = this.read;
    this.boats.push(b);
    return b;
  }

  remove(b: Boat): void {
    b.removed = true;
    for (let i = 0; i < b.riders.length; i++) if (b.riders[i] !== PLAYER) b.riders[i] = null;
    this.boats = this.boats.filter((o) => o !== b);
  }

  clear(): void { for (const b of this.boats) b.removed = true; this.boats = []; }

  /** Nearest boat on the ray. */
  raycast(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, max: number): { boat: Boat; dist: number } | null {
    let best: { boat: Boat; dist: number } | null = null;
    for (const b of this.boats) {
      const t = b.rayDist(ox, oy, oz, dx, dy, dz, max);
      if (t >= 0 && (!best || t < best.dist)) best = { boat: b, dist: t };
    }
    return best;
  }

  tick(): void {
    for (const b of this.boats.slice()) {
      if (b.removed) continue;
      if (!this.loaded(Math.floor(b.x), Math.floor(b.z))) continue;
      this.tickBoat(b);
      if (b.y < -20 && !b.removed) { this.events.onBreak?.(b, false); this.remove(b); }
    }
  }

  // ----------------------------------------------------------------- physics

  private tickBoat(b: Boat): void {
    b.px = b.x; b.py = b.y; b.pz = b.z; b.pyaw = b.yaw;
    b.paddlePrev[0] = b.paddle[0]; b.paddlePrev[1] = b.paddle[1];
    b.oldStatus = b.status;
    b.status = this.statusOf(b);
    if (b.status === 'under' || b.status === 'underFlow') b.underTicks++; else b.underTicks = 0;
    if (b.underTicks >= 60) { for (let i = 0; i < b.riders.length; i++) if (b.riders[i] !== PLAYER) b.riders[i] = null; }
    if (b.hurtTime > 0) b.hurtTime--;
    if (b.damage > 0) b.damage--;
    this.floatBoat(b);
    this.controlBoat(b);
    this.moveBoat(b);
    // paddle phase: advances while the matching side paddles
    b.paddle[0] = b.pl ? b.paddle[0] + Math.PI / 8 : 0;
    b.paddle[1] = b.pr ? b.paddle[1] + Math.PI / 8 : 0;
    if (!b.hasPlayer && !b.occupied) b.clearInput();
  }

  private statusOf(b: Boat): BoatStatus {
    const u = this.underwater(b);
    if (u) return u;
    if (this.inWater(b)) return 'water';
    const f = this.groundFriction(b);
    if (f > 0) { b.landFriction = f; return 'land'; }
    return 'air';
  }

  private underwater(b: Boat): BoatStatus | null {
    const top = b.y + BOAT_H;
    const d = top + 0.001;
    const y0 = Math.floor(top), y1 = Math.ceil(d);
    let under = false;
    for (let y = y0; y < y1; y++)
      for (let z = Math.floor(b.z - HALF); z < Math.ceil(b.z + HALF); z++)
        for (let x = Math.floor(b.x - HALF); x < Math.ceil(b.x + HALF); x++) {
          const h = waterHeight(this.read, x, y, z);
          if (h > 0 && d < y + h) {
            if (IS_WATER_SOURCE[this.read(x, y, z)] === 0) return 'underFlow';
            under = true;
          }
        }
    if (under) { b.waterLevel = top; return 'under'; }
    return null;
  }

  private inWater(b: Boat): boolean {
    const y0 = Math.floor(b.y), y1 = Math.ceil(b.y + 0.001);
    let level = -Infinity, hit = false;
    for (let y = y0; y < y1; y++)
      for (let z = Math.floor(b.z - HALF); z < Math.ceil(b.z + HALF); z++)
        for (let x = Math.floor(b.x - HALF); x < Math.ceil(b.x + HALF); x++) {
          const h = waterHeight(this.read, x, y, z);
          if (h > 0) { level = Math.max(level, y + h); hit = hit || b.y < y + h; }
        }
    b.waterLevel = level;
    return hit;
  }

  /** Average block slipperiness under the hull (0: nothing to stand on). */
  private groundFriction(b: Boat): number {
    const y = Math.floor(b.y - 0.001);
    let sum = 0, n = 0;
    for (let z = Math.floor(b.z - HALF); z < Math.ceil(b.z + HALF); z++)
      for (let x = Math.floor(b.x - HALF); x < Math.ceil(b.x + HALF); x++) {
        const id = this.read(x, y, z);
        if (IS_WATERY[id] === 1) continue;
        const s = y < 0 ? FULL_CELL_BOXES : collisionOf(id);
        let top = -1;
        for (let k = 0; k < s.length; k += 6) top = Math.max(top, s[k + 4] / 16 + y);
        if (top >= b.y - 0.001) { sum += SLIP.get(id) ?? 0.6; n++; }
      }
    return n ? sum / n : 0;
  }

  private waterLevelAbove(b: Boat): number {
    const top = Math.floor(b.y + BOAT_H);
    const x = Math.floor(b.x), z = Math.floor(b.z);
    for (let y = top; y < top + 8; y++) {
      const h = waterHeight(this.read, x, y, z);
      if (h <= 0) return y;
      if (h < 1) return y + h;
    }
    return top + 8;
  }

  private floatBoat(b: Boat): void {
    let e = -0.04, f = 0, inv = 0.05;
    if (b.oldStatus === 'air' && b.status !== 'air' && b.status !== 'land') {
      b.waterLevel = b.y + BOAT_H;
      b.y = this.waterLevelAbove(b) - BOAT_H + 0.101;
      b.vy = 0;
      b.status = 'water';
      return;
    }
    if (b.status === 'water') { f = (b.waterLevel - b.y) / BOAT_H; inv = 0.9; }
    else if (b.status === 'underFlow') { e = -7e-4; inv = 0.9; }
    else if (b.status === 'under') { f = 0.01; inv = 0.45; }
    else if (b.status === 'air') inv = 0.9;
    else if (b.status === 'land') inv = b.landFriction;
    b.vx *= inv; b.vz *= inv; b.vy += e;
    b.deltaRot *= inv;
    if (f > 0) b.vy = (b.vy + f * 0.06153846016296973) * 0.75;
    // current: flowing water drags the hull (vanilla via the fluid pushing code)
    if (b.status === 'water' || b.status === 'underFlow') this.pushByFlow(b);
  }

  private pushByFlow(b: Boat): void {
    let fx = 0, fz = 0, n = 0;
    const flow = this.flow;
    for (let z = Math.floor(b.z - HALF); z < Math.ceil(b.z + HALF); z++)
      for (let x = Math.floor(b.x - HALF); x < Math.ceil(b.x + HALF); x++) {
        const y = Math.floor(b.y + 0.001);
        if (waterHeight(this.read, x, y, z) > 0) { waterFlow(this.read, x, y, z, flow); fx += flow[0]; fz += flow[2]; n++; }
      }
    if (n > 0) { b.vx += (fx / n) * 0.014; b.vz += (fz / n) * 0.014; }
  }

  private controlBoat(b: Boat): void {
    const driven = b.riders[0] === PLAYER;
    if (!driven) { b.pl = b.pr = false; return; }
    const l = b.inL, r = b.inR, up = b.inUp, down = b.inDown;
    let f = 0;
    if (l) b.deltaRot -= 1;
    if (r) b.deltaRot += 1;
    if (r !== l && !up && !down) f += 0.005;
    b.yaw -= (b.deltaRot * Math.PI) / 180;
    if (up) f += 0.04;
    if (down) f -= 0.005;
    b.vx += -Math.sin(b.yaw) * f;
    b.vz += -Math.cos(b.yaw) * f;
    b.pl = (r && !l) || up;
    b.pr = (l && !r) || up;
  }

  private moveBoat(b: Boat): void {
    const box = b.aabb();
    const dy = clipMove(box, 1, b.vy, EPS, this.shapesAt);
    box[1] += dy; box[4] += dy;
    const dx = clipMove(box, 0, b.vx, EPS, this.shapesAt);
    box[0] += dx; box[3] += dx;
    const dz = clipMove(box, 2, b.vz, EPS, this.shapesAt);
    box[2] += dz; box[5] += dz;
    b.onGround = b.vy < 0 && dy !== b.vy;
    const fall = -dy;
    if (Math.abs(dx - b.vx) > 1e-9) b.vx = 0;
    if (Math.abs(dz - b.vz) > 1e-9) b.vz = 0;
    if (Math.abs(dy - b.vy) > 1e-9) b.vy = 0;
    b.x += dx; b.y += dy; b.z += dz;
    // fall bookkeeping (Boat.checkFallDamage): water resets it, a hard landing in the air wrecks the hull
    if (b.status === 'water' || b.status === 'under' || b.status === 'underFlow') b.fallDistance = 0;
    else if (b.onGround) {
      if (b.fallDistance > 3 && b.oldStatus === 'air') {
        this.events.onBreak?.(b, true); this.remove(b);
        return;
      }
      b.fallDistance = 0;
    } else if (fall > 0) b.fallDistance += fall;
    b.y = clamp(b.y, -64, 4096);
  }

  // ---------------------------------------------------------------- saving

  serialize(): number[][] {
    const r = (v: number): number => Math.round(v * 1000) / 1000;
    return this.boats.filter((b) => !b.removed).map((b) => [b.wood, r(b.x), r(b.y), r(b.z), r(b.yaw), Math.round(b.damage)]);
  }

  load(rows: unknown): void {
    this.clear();
    if (!Array.isArray(rows)) return;
    for (const r of rows) {
      if (!Array.isArray(r) || r.length < 5 || r.slice(0, 5).some((v) => typeof v !== 'number' || !Number.isFinite(v))) continue;
      const w = r[0] as number;
      if (w < 0 || w >= BOAT_ITEMS.length) continue;
      const b = this.add(w, r[1] as number, r[2] as number, r[3] as number, r[4] as number);
      b.damage = typeof r[5] === 'number' ? (r[5] as number) : 0;
    }
  }
}
